import type {
    IRegDataComparisonResult,
    IRegDataPreviewRequest,
    IRegDataStagedChange,
} from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import {
    buildInitialEditedValue,
    getStagedChangeKey,
    RegDataChangeTypes,
} from "./regDataAlignmentDisplay";
import type { ReconciliationGridRow } from "./reconciliationGridTypes";

export function stageFromReconciliationRow(row: ReconciliationGridRow): IRegDataStagedChange | null {
    if (row.actionDisabled || !row.stageChangeType || row.isStaged) return null;

    const change: IRegDataStagedChange = {
        fieldKey: row.fieldKey,
        changeType: row.stageChangeType,
        templateRawValue: row.templateSide.templateRawValue,
        systemRecordId: row.systemSide.systemRecordId ?? null,
    };

    if (
        row.stageChangeType === RegDataChangeTypes.AddNewFromTemplate ||
        row.stageChangeType === RegDataChangeTypes.SyncFlagsFromTemplate ||
        row.stageChangeType === RegDataChangeTypes.SyncHazardousFromTemplate
    ) {
        if (!row.templateSide.templateOption) return null;
        change.editedValue = buildInitialEditedValue(row.fieldKey, row.templateSide.templateOption);
    }

    return change;
}

export function stageSoftDeleteFromGridRow(row: ReconciliationGridRow): IRegDataStagedChange | null {
    if (!row.systemSide.systemRecordId) return null;
    return {
        fieldKey: row.fieldKey,
        changeType: RegDataChangeTypes.SoftDeleteExisting,
        systemRecordId: row.systemSide.systemRecordId,
        templateRawValue: null,
    };
}

export function addStagedChange(
    stagedChanges: IRegDataStagedChange[],
    change: IRegDataStagedChange
): IRegDataStagedChange[] {
    const key = getStagedChangeKey(change);
    const filtered = stagedChanges.filter((existing) => getStagedChangeKey(existing) !== key);
    return [...filtered, change];
}

export function addStagedChanges(
    stagedChanges: IRegDataStagedChange[],
    changes: IRegDataStagedChange[]
): IRegDataStagedChange[] {
    if (changes.length === 0) return stagedChanges;

    const byKey = new Map<string, IRegDataStagedChange>();
    for (const existing of stagedChanges) {
        byKey.set(getStagedChangeKey(existing), existing);
    }
    for (const change of changes) {
        byKey.set(getStagedChangeKey(change), change);
    }
    return Array.from(byKey.values());
}

export function removeStagedChange(
    stagedChanges: IRegDataStagedChange[],
    changeKey: string
): IRegDataStagedChange[] {
    return stagedChanges.filter((change) => getStagedChangeKey(change) !== changeKey);
}

export function removeStagedChanges(
    stagedChanges: IRegDataStagedChange[],
    changeKeys: string[]
): IRegDataStagedChange[] {
    if (changeKeys.length === 0) return stagedChanges;

    const removeKeys = new Set(changeKeys);
    return stagedChanges.filter((change) => !removeKeys.has(getStagedChangeKey(change)));
}

export function resetStagedChanges(): IRegDataStagedChange[] {
    return [];
}

export function updateStagedEditedValue(
    stagedChanges: IRegDataStagedChange[],
    changeKey: string,
    editedValue: Record<string, unknown>
): IRegDataStagedChange[] {
    return stagedChanges.map((change) =>
        getStagedChangeKey(change) === changeKey ? { ...change, editedValue: { ...editedValue } } : change
    );
}

export function buildPreviewRequest(stagedChanges: IRegDataStagedChange[]): IRegDataPreviewRequest {
    return {
        stagedChanges,
        acknowledgedWarnings: [],
    };
}

export function hasPendingStagedChanges(stagedChanges: IRegDataStagedChange[]): boolean {
    return stagedChanges.length > 0;
}

export function computeStagedChangesSignature(stagedChanges: IRegDataStagedChange[]): string {
    const normalized = [...stagedChanges]
        .map((change) => ({
            fieldKey: change.fieldKey,
            changeType: change.changeType,
            systemRecordId: change.systemRecordId ?? null,
            templateRawValue: change.templateRawValue ?? null,
            editedValue: change.editedValue ?? null,
        }))
        .sort((a, b) => {
            const left = `${a.fieldKey}|${a.changeType}|${a.systemRecordId ?? ""}|${a.templateRawValue ?? ""}`;
            const right = `${b.fieldKey}|${b.changeType}|${b.systemRecordId ?? ""}|${b.templateRawValue ?? ""}`;
            return left.localeCompare(right);
        });

    return JSON.stringify(normalized);
}

export interface SoftDeleteConfirmContext {
    fieldKey: string;
    fieldLabel: string;
    systemRecordId: string;
    displayValue: string;
    usageMessages: string[];
    usageCountTotal: number;
}

export function buildSoftDeleteConfirmContextFromGridRow(
    row: ReconciliationGridRow,
    fieldLabel: string,
    comparison: IRegDataComparisonResult | null
): SoftDeleteConfirmContext {
    const systemRecordId = row.systemSide.systemRecordId ?? "";
    const card = comparison?.fields?.find((f) => f.fieldKey === row.fieldKey);
    const hints = (card?.usageHints ?? []).filter((hint) => hint.systemRecordId === systemRecordId);
    const usageMessages = hints.map((hint) => hint.message);
    const usageCountTotal = hints.reduce((sum, hint) => sum + hint.count, 0);

    return {
        fieldKey: row.fieldKey,
        fieldLabel,
        systemRecordId,
        displayValue: row.systemSide.displayValue ?? systemRecordId,
        usageMessages,
        usageCountTotal,
    };
}
